import { getAbsoluteUrl } from "./urls";
import { fetchAuthenticated } from "./utils";
import { HTTP_OK } from "../utils/constants/http";
import { FAILED, SUCCESS } from "../utils/constants/tags";

export interface JWTRequestStatus {
  status: string;
  message?: string;
  accessToken?: string;
  email?: string;
}
export interface UserDataRequestStatus {
  status: string;
  message?: string;
  userData?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    isSuperUser: boolean;
  };
}

interface SuccessfulJWTRequest {
  status: string;
  access: string;
  refresh: string;
  email: string | null;
}
interface FailedJWTRequest {
  status: string;
  message: string;
}
interface SuccessfulUserDataRequest {
  status: string;
  userData: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    isSuperUser: boolean;
  };
}
interface FailedUserDataRequest {
  status: string;
  message: string;
}

type JWTResponse = SuccessfulJWTRequest | FailedJWTRequest;
type UserDataResponse = SuccessfulUserDataRequest | FailedUserDataRequest;

export const getJWTToken = async (
  email: string,
  password: string
): Promise<JWTRequestStatus> => {
  const url = getAbsoluteUrl("/auth/token/");
  const data = {
    email: email,
    password: password,
  };
  let response: Response = { ok: false } as Response;
  try {
    response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify(data),
    });
  } catch (error) {
    return {
      status: FAILED,
      message: "Server not Found",
    };
  }

  if (!response.ok) {
    return {
      status: FAILED,
      message: "Network Error",
    };
  }
  const responseData: JWTResponse = await response.json();

  if (response.status !== HTTP_OK) {
    let failedResponse = responseData as FailedJWTRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }

  let successfulResponse = responseData as SuccessfulJWTRequest;

  return {
    status: SUCCESS,
    accessToken: successfulResponse.access,
  };
};

export const refreshJWTToken = async (): Promise<JWTRequestStatus> => {
  const url = getAbsoluteUrl("/auth/token_refresh/");
  const data = {};
  let response: Response = { ok: false } as Response;
  try {
    response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify(data),
    });
  } catch (error) {
    return {
      status: FAILED,
      message: "Server not Found",
    };
  }

  if (!response.ok) {
    return {
      status: FAILED,
      message: "Network Error",
    };
  }

  const responseData: JWTResponse = await response.json();

  if (response.status !== HTTP_OK) {
    let failedResponse = responseData as FailedJWTRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }
  let successfulResponse = responseData as SuccessfulJWTRequest;

  return {
    status: SUCCESS,
    accessToken: successfulResponse.access,
  };
};
export const clearRefreshToken = async (): Promise<JWTRequestStatus> => {
  const url = getAbsoluteUrl("/auth/clear_refresh_token/");
  const data = {};
  let response: Response = { ok: false } as Response;
  try {
    response = await fetchAuthenticated(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify(data),
    });
  } catch (error) {
    return {
      status: FAILED,
      message: "Server not Found",
    };
  }
  if (!response.ok) {
    return {
      status: FAILED,
      message: "Network Error",
    };
  }

  const responseData: JWTResponse = await response.json();

  if (response.status !== HTTP_OK) {
    let failedResponse = responseData as FailedJWTRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }

  return {
    status: SUCCESS,
    accessToken: "",
  };
};
export const getUserData = async (): Promise<UserDataRequestStatus> => {
  const url = getAbsoluteUrl("/auth/user/");
  let response: Response = { ok: false } as Response;
  try {
    response = await fetchAuthenticated(url, {
      method: "GET",
      credentials: "include",
    });
  } catch (error) {
    return {
      status: FAILED,
      message: "Server not Found",
    };
  }

  if (!response.ok) {
    return {
      status: FAILED,
      message: "Network Error",
    };
  }

  const responseData: UserDataResponse = await response.json();

  if (response.status !== HTTP_OK) {
    let failedResponse = responseData as FailedUserDataRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }

  if (responseData.status !== SUCCESS) {
    let failedResponse = responseData as FailedUserDataRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }

  let successfulResponse = responseData as SuccessfulUserDataRequest;

  return {
    status: SUCCESS,
    userData: {
      id: successfulResponse.userData.id,
      username: successfulResponse.userData.username,
      firstName: successfulResponse.userData.firstName,
      lastName: successfulResponse.userData.lastName,
      email: successfulResponse.userData.email,
      isSuperUser: successfulResponse.userData.isSuperUser,
    },
  };
};

export interface ForgotPasswordRequestStatus {
  status: string;
  message?: string;
}

export interface ResetPasswordRequestStatus {
  status: string;
  message?: string;
}

interface SuccessfulForgotPasswordRequest {
  status: string;
  message: string;
}

interface FailedForgotPasswordRequest {
  status: string;
  message: string;
}

interface SuccessfulResetPasswordRequest {
  status: string;
  message: string;
}

interface FailedResetPasswordRequest {
  status: string;
  message: string;
}

type ForgotPasswordResponse =
  | SuccessfulForgotPasswordRequest
  | FailedForgotPasswordRequest;
type ResetPasswordResponse =
  | SuccessfulResetPasswordRequest
  | FailedResetPasswordRequest;

// Agregar estas funciones al final del archivo

export const forgotPasswordRequest = async (
  email: string
): Promise<ForgotPasswordRequestStatus> => {
  const url = getAbsoluteUrl("/auth/forgot-password/");
  const data = {
    email: email,
  };
  let response: Response = { ok: false } as Response;
  try {
    response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify(data),
    });
  } catch (error) {
    return {
      status: FAILED,
      message: "Server not Found",
    };
  }

  if (!response.ok) {
    return {
      status: FAILED,
      message: "Network Error",
    };
  }

  const responseData: ForgotPasswordResponse = await response.json();

  if (response.status !== HTTP_OK) {
    let failedResponse = responseData as FailedForgotPasswordRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }

  let successfulResponse = responseData as SuccessfulForgotPasswordRequest;

  return {
    status: SUCCESS,
    message: successfulResponse.message,
  };
};

export const resetPasswordRequest = async (
  token: string,
  password: string
): Promise<ResetPasswordRequestStatus> => {
  const url = getAbsoluteUrl("/auth/reset-password/");
  const data = {
    token: token,
    password: password,
  };
  let response: Response = { ok: false } as Response;
  try {
    response = await fetch(url, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify(data),
    });
  } catch (error) {
    return {
      status: FAILED,
      message: "Server not Found",
    };
  }

  if (!response.ok) {
    return {
      status: FAILED,
      message: "Network Error",
    };
  }

  const responseData: ResetPasswordResponse = await response.json();

  if (response.status !== HTTP_OK) {
    let failedResponse = responseData as FailedResetPasswordRequest;
    return {
      status: FAILED,
      message: failedResponse.message,
    };
  }

  let successfulResponse = responseData as SuccessfulResetPasswordRequest;

  return {
    status: SUCCESS,
    message: successfulResponse.message,
  };
};
